import { useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import { styles } from "../../styles/pages/Category/CategoryAddStyles";

const CategoryAdd = () => {
  const navigate = useNavigate();
  const [prodCatName, setProdCatName] = useState("");
  const [prodCatDesc, setProdCatDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{
    prod_cat_name?: string;
    prod_cat_desc?: string;
  }>({});
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    if (!prodCatName.trim()) {
      setErrors({ prod_cat_name: "The Category name field is required" });
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("prod_cat_name", prodCatName);
    // formData.append("prod_cat_desc", prodCatDesc);

    const userId = sessionStorage.getItem("user_id");
    const token = sessionStorage.getItem("access_token");

    if (!token) {
      setErrors({ prod_cat_name: "Authorization required. Please log in." });
      setSaving(false);
      navigate("/signin");
      return;
    }

    if (!userId) {
      setErrors({ prod_cat_name: "User ID required. Please log in again." });
      setSaving(false);
      navigate("/signin");
      return;
    }

    formData.append("user_id", userId);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/category/add`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          navigate("/category-list");
        }, 2000);
        setProdCatName("");
        setProdCatDesc("");
        return;
      }
      if (response.data.status === 422 && response.data.message) {
        setErrors(
          Object.fromEntries(
            Object.entries(response.data.message).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ])
          )
        );
        return;
      }

      if (response.data.status === 500) {
        setErrors({
          prod_cat_name: response.data.message || "Internal Server Error",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      setErrors({ prod_cat_name: errorMessage });

      if (error.response?.status === 422 && error.response.data.errors) {
        setErrors(
          Object.fromEntries(
            Object.entries(error.response.data.errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ])
          )
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "prod_cat_name") {
      setProdCatName(value);
      if (!value.trim()) {
        setErrors({
          ...errors,
          prod_cat_name: "The Category name field is required",
        });
      } else {
        setErrors({ ...errors, prod_cat_name: undefined });
      }
    } else if (name === "prod_cat_desc") {
      setProdCatDesc(value);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Add Category</h2>
      </div>

      <div className={styles.formContainer}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputContainer}>
            <div>
              <label className={styles.label}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="prod_cat_name"
                value={prodCatName}
                onChange={handleInputChange}
                className={`${styles.input} ${
                  errors.prod_cat_name
                    ? styles.inputError
                    : "border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10"
                }`}
              />
              {errors.prod_cat_name && (
                <span className={styles.errorText}>{errors.prod_cat_name}</span>
              )}
            </div>
            <div>
              <label className={styles.label}>Description</label>
              <textarea
                name="prod_cat_desc"
                value={prodCatDesc}
                onChange={handleInputChange}
                className={`${styles.input} border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 dark:focus:border-brand-800`}
              />
            </div>
          </div>

          <div className={styles.buttonContainer}>
            <button
              type="submit"
              disabled={saving}
              className={styles.submitButton}
            >
              {saving ? "Saving..." : "Submit"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/category-list")}
              className={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </form>

        <Modal
          isOpen={isSuccessModalOpen}
          onClose={() => setSuccessModalOpen(false)}
          className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
        >
          <button
            onClick={() => setSuccessModalOpen(false)}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          ></button>

          <div className="flex justify-center">
            <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>

          <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
            Category Added Successfully
          </h4>

          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            The category has been added to the system successfully.
          </p>

          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setSuccessModalOpen(false)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              OK
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default CategoryAdd;
